import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc, limit } from 'firebase/firestore';
import { db } from '../utils/firebase-config';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import ProfileActivity, { useProfileActivity } from '../Components/ProfileActivity';

/**
 * A member's public profile (soaplab.in/username): their name, a line about them and what they
 * have shared with the community.
 */
export default function PublicProfilePage() {
  const { userId, username } = useParams();
  const { user } = useAuth();
  const [uid, setUid] = useState(userId || null);
  const [profile, setProfile] = useState(null);
  const [lookup, setLookup] = useState('loading'); // loading | found | missing
  const activity = useProfileActivity(lookup === 'found' ? uid : null);

  useEffect(() => {
    let active = true;
    const find = async () => {
      setLookup('loading');
      try {
        let id = userId || null;
        let data = null;
        if (!id && username) {
          const handle = username.toLowerCase();
          const handleSnap = await getDoc(doc(db, 'usernames', handle));
          if (handleSnap.exists()) id = handleSnap.data().uid;
          if (!id) {
            const match = await getDocs(query(collection(db, 'publicProfiles'), where('usernameLower', '==', handle), limit(1)));
            if (!match.empty) { data = match.docs[0].data(); id = data.uid; }
          }
        }
        if (id && !data) {
          const snap = await getDoc(doc(db, 'publicProfiles', id));
          if (snap.exists()) data = snap.data();
        }
        if (!active) return;
        setUid(id);
        setProfile(data);
        setLookup(data ? 'found' : 'missing');
      } catch (err) {
        console.error('Failed to load public profile:', err);
        if (active) setLookup('missing');
      }
    };
    find();
    return () => { active = false; };
  }, [userId, username]);

  const displayName = profile?.displayName || 'Soap maker';

  if (lookup === 'missing') {
    return (
      <div className="sl-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Community', to: '/community' }, { label: 'Not found' }]} />
          <div className="pageheading">Profile not found</div>
          <div className="pagedescription">There’s no SoapLab member at this address. The link may be mistyped, or the member changed their username.</div>
        </div>
        <div className="borderleft">
          <section className="sl-band">
            <div className="sl-actions">
              <Link to="/community" className="sl-btn sl-btn-primary">Go to the community</Link>
              <Link to="/" className="sl-arrow-link">Homepage</Link>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="sl-page">
      <div className="pageheader borderleft">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Community', to: '/community' }, { label: lookup === 'found' ? displayName : 'Profile' }]} />
        <div className="profile-identity">
          <div className="profile-avatar">
            {profile?.photoURL ? <img src={profile.photoURL} alt="" /> : <span>{displayName.charAt(0).toUpperCase()}</span>}
          </div>
          <div>
            <h1 className="pageheading profile-name">{lookup === 'found' ? displayName : 'Loading…'}</h1>
            {profile?.username && <p className="profile-handle">@{profile.username}</p>}
          </div>
        </div>
        {profile?.bio && <p className="sl-tagline profile-bio">{profile.bio}</p>}
        {user && uid === user.uid && (
          <div className="sl-actions profile-actions">
            <Link to="/profile" className="sl-arrow-link">This is you: edit your profile</Link>
          </div>
        )}
      </div>
      <div className="borderleft">
        {lookup === 'found' && <ProfileActivity activity={activity} isOwn={false} name={displayName} />}
      </div>
      <Footer />
    </div>
  );
}
